/**
 * PushCampaign — Feature #21 (web)
 * GET  /api/broadcast/push/estimate — live reach count before sending
 * GET  /api/broadcast/push/history  — paginated campaign log
 * POST /api/broadcast/push          — send push campaign to a segment
 *
 * Roles: admin, centreAdmin, owner (enforced in App.js + Sidebar filter)
 * Rate limit: 5 per centre per day — surfaced in result banner on RATE_LIMITED code
 * DPDPA §5: consent filter enforced server-side; notice visible in compose tab
 */

import React, { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import { getPushEstimate, getPushHistory, sendPushCampaign } from '../utils/api';
import { useWindowSize } from '../utils/useWindowSize';

const TITLE_MAX   = 50;
const MESSAGE_MAX = 160;

// Keys and labels must match CampaignScreen.js SEGMENTS exactly (Feature #21 source of truth)
const SEGMENTS = [
  { key: 'all_patients',      label: 'All Patients',      desc: 'All patients with push consent' },
  { key: 'inactive_30d',      label: 'Inactive 30 days',  desc: 'No activity in last 30 days' },
  { key: 'inactive_60d',      label: 'Inactive 60 days',  desc: 'No activity in last 60 days' },
  { key: 'package_holders',   label: 'Package Holders',   desc: 'Patients with an active package' },
  { key: 'upcoming_sessions', label: 'Upcoming Sessions', desc: 'Scheduled session in next 7 days' },
  { key: 'completed_program', label: 'Completed Program', desc: 'All sessions done, no active case' },
  { key: 'condition_based',   label: 'By Condition',      desc: 'Patients with a specific condition keyword' },
  { key: 'all_healers',       label: 'All Healers',       desc: 'Healers at this centre with push consent' },
];

function segLabel(key) {
  var seg = SEGMENTS.find(function(s) { return s.key === key; });
  return seg ? seg.label : key;
}

export default function PushCampaign() {
  const { isMobile } = useWindowSize();
  const [activeTab,        setActiveTab]        = useState('compose'); // 'compose' | 'history'
  const [segment,          setSegment]          = useState('');
  const [conditionKeyword, setConditionKeyword] = useState('');
  const [title,            setTitle]            = useState('');
  const [message,          setMessage]          = useState('');
  const [estimate,         setEstimate]         = useState(null);   // number | null
  const [estimating,       setEstimating]       = useState(false);
  const [sending,          setSending]          = useState(false);
  const [result,           setResult]           = useState(null);   // { ok: bool, text: string }
  const [history,          setHistory]          = useState([]);
  const [historyLoading,   setHistoryLoading]   = useState(false);

  // Fetch estimate on segment or keyword change — mirrors Announcements.js lines 24-41
  useEffect(function() {
    if (!segment || (segment === 'condition_based' && !conditionKeyword.trim())) {
      setEstimate(null);
      return;
    }
    setEstimating(true);
    getPushEstimate(segment, segment === 'condition_based' ? conditionKeyword.trim() : '')
      .then(function(res) { setEstimate(res.data.estimatedReach ?? 0); })
      .catch(function()   { setEstimate(null); })
      .finally(function() { setEstimating(false); });
  }, [segment, conditionKeyword]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch history when tab switches — mirrors Announcements.js lines 70-77
  useEffect(function() {
    if (activeTab !== 'history') return;
    setHistoryLoading(true);
    getPushHistory()
      .then(function(res) { setHistory(res.data.data || []); })
      .catch(function()   { setHistory([]); })
      .finally(function() { setHistoryLoading(false); });
  }, [activeTab]);

  // Send handler — window.confirm() mirrors Announcements.js line 57 exactly
  var handleSend = async function() {
    if (!segment)        { alert('Please select a recipient group.');    return; }
    if (!title.trim())   { alert('Please enter a notification title.');  return; }
    if (!message.trim()) { alert('Please enter a message.');             return; }
    if (segment === 'condition_based' && !conditionKeyword.trim()) {
      alert('Please enter a condition keyword.'); return;
    }
    var reachText = estimate !== null
      ? ' (~' + estimate + ' eligible recipient' + (estimate !== 1 ? 's' : '') + ')'
      : '';
    if (!window.confirm(
      'Send "' + title.trim() + '" to ' + segLabel(segment) + reachText + '?\n\n' +
      'Only consenting recipients will be notified.'
    )) return;
    setSending(true);
    setResult(null);
    try {
      var body = { segment, title: title.trim(), message: message.trim() };
      if (segment === 'condition_based') body.conditionKeyword = conditionKeyword.trim();
      var res = await sendPushCampaign(body);
      var sent = res.data.sentCount ?? 0;
      if (sent === 0) {
        setResult({ ok: false, text: 'No eligible recipients with push consent found.' });
      } else {
        setResult({ ok: true, text: 'Push sent to ' + sent + ' recipient' + (sent !== 1 ? 's' : '') + '.' });
        setTitle(''); setMessage(''); setSegment(''); setConditionKeyword(''); setEstimate(null);
      }
    } catch (err) {
      var code = err.response?.data?.code;
      setResult({ ok: false, text: code === 'RATE_LIMITED'
        ? 'Limit reached. Maximum 5 push campaigns per centre per day.'
        : (err.response?.data?.message || 'Could not send campaign. Please try again.') });
    } finally {
      setSending(false);
    }
  };

  var canSend = !!segment && !!title.trim() && !!message.trim() && !sending;

  // Inline styles — mirrors Announcements.js s object structure exactly
  var s = {
    layout:      { display: 'flex', minHeight: '100vh', background: '#f5f8f5', fontFamily: "'Open Sans', sans-serif" },
    main:        { marginLeft: isMobile ? 0 : '260px', flex: 1, padding: isMobile ? '56px 16px 16px' : '32px' },
    header:      { marginBottom: '28px' },
    pageTitle:   { fontFamily: "'Raleway', sans-serif", fontSize: '26px', fontWeight: '700', color: '#1a1a2e', margin: '0 0 4px' },
    sub:         { fontSize: '13px', color: '#666', margin: 0 },
    card:        { background: '#fff', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '28px', marginBottom: '20px' },
    sectionTitle:{ fontFamily: "'Raleway', sans-serif", fontSize: '13px', fontWeight: '700', color: '#1a1a2e', letterSpacing: '0.5px', marginBottom: '14px' },
    label:       { display: 'block', fontSize: '10px', fontWeight: '700', letterSpacing: '1.5px', textTransform: 'uppercase', color: '#666', marginBottom: '6px', fontFamily: "'Raleway', sans-serif" },
    input:       { width: '100%', padding: '10px 14px', border: '1px solid #e0e0e0', borderRadius: '5px', fontSize: '14px', fontFamily: "'Open Sans', sans-serif", boxSizing: 'border-box', color: '#333' },
    textarea:    { width: '100%', padding: '14px', border: '1px solid #e0e0e0', borderRadius: '5px', fontSize: '14px', fontFamily: "'Open Sans', sans-serif", boxSizing: 'border-box', color: '#333', resize: 'vertical', minHeight: '100px', lineHeight: '1.7' },
    tabs:        { display: 'flex', gap: '4px', background: '#fff', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '6px', marginBottom: '24px', width: 'fit-content' },
    tab:         { padding: '10px 28px', background: 'transparent', border: 'none', borderRadius: '6px', fontFamily: "'Raleway', sans-serif", fontSize: '13px', fontWeight: '700', color: '#666', cursor: 'pointer' },
    tabActive:   { background: '#4a8f82', color: '#fff' },
    sendBtn:     { background: '#4a8f82', color: '#fff', border: 'none', borderRadius: '4px', padding: '13px 32px', fontFamily: "'Raleway', sans-serif", fontSize: '13px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase', cursor: 'pointer' },
    charCount:   { fontSize: '11px', color: '#999', textAlign: 'right', marginTop: '4px' },
    dpdpaBox:    { background: '#eaf4fb', border: '1px solid #aed9f5', borderRadius: '6px', padding: '12px 16px', marginBottom: '20px', fontSize: '13px', color: '#2980b9', lineHeight: '1.5' },
    estimateRow: { background: '#f9faf7', borderRadius: '6px', padding: '12px 16px', marginTop: '14px', fontSize: '13px', color: '#4a8f82', fontWeight: '600' },
    resultBox:   { borderRadius: '6px', padding: '16px 20px', marginBottom: '20px', fontSize: '14px', fontWeight: '700' },
  };

  return (
    <div style={s.layout}>
      <Sidebar />
      <div style={s.main}>

        <div style={s.header}>
          <h1 style={s.pageTitle}>Push Campaign</h1>
          <p style={s.sub}>Send push notifications to specific patient and healer segments</p>
        </div>

        {/* Tabs — Announcements.js lines 138-156 */}
        <div style={s.tabs}>
          <button style={activeTab === 'compose' ? { ...s.tab, ...s.tabActive } : s.tab}
            onClick={function() { setActiveTab('compose'); }}>
            Compose &amp; Send
          </button>
          <button style={activeTab === 'history' ? { ...s.tab, ...s.tabActive } : s.tab}
            onClick={function() { setActiveTab('history'); }}>
            History
          </button>
        </div>

        {/* COMPOSE TAB */}
        {activeTab === 'compose' && (
          <div>
            {/* DPDPA §5 notice */}
            <div style={s.dpdpaBox}>
              Only recipients with notification consent receive push campaigns. Rate limit: 5 per centre per day.
            </div>

            {/* Result banner — Announcements.js lines 257-285 */}
            {result && (
              <div style={{ ...s.resultBox, background: result.ok ? '#eafaf1' : '#fdf0f0', border: '1px solid ' + (result.ok ? '#27ae60' : '#e74c3c'), color: result.ok ? '#1a5c38' : '#c0392b' }}>
                {result.text}
                <button onClick={function() { setResult(null); }}
                  style={{ marginLeft: '16px', background: 'transparent', border: '1px solid', borderRadius: '4px', padding: '4px 12px', fontSize: '12px', fontWeight: '700', cursor: 'pointer', color: 'inherit' }}>
                  Dismiss
                </button>
              </div>
            )}

            {/* Step 1: Recipient Group */}
            <div style={s.card}>
              <div style={s.sectionTitle}>Step 1 — Recipient Group</div>
              <label style={s.label}>Segment</label>
              <select style={s.input} value={segment}
                onChange={function(e) { setSegment(e.target.value); setConditionKeyword(''); setEstimate(null); setResult(null); }}>
                <option value="">Select a recipient group…</option>
                {SEGMENTS.map(function(seg) {
                  return <option key={seg.key} value={seg.key}>{seg.label} — {seg.desc}</option>;
                })}
              </select>

              {/* Condition keyword — shown only for condition_based, mirrors CampaignScreen.js lines 236-253 */}
              {segment === 'condition_based' && (
                <div style={{ marginTop: '14px' }}>
                  <label style={s.label}>Condition Keyword</label>
                  <input style={s.input} type="text"
                    placeholder="e.g. anxiety, back pain…"
                    value={conditionKeyword}
                    onChange={function(e) { setConditionKeyword(e.target.value); }} />
                </div>
              )}

              {/* Estimated reach — Announcements.js count display pattern lines 188-205 */}
              {segment && (
                <div style={s.estimateRow}>
                  {estimating
                    ? 'Calculating reach…'
                    : estimate === null
                      ? (segment === 'condition_based' ? 'Enter keyword above to see estimate' : '—')
                      : '~' + estimate + ' eligible recipient' + (estimate !== 1 ? 's' : '') + ' (with push consent)'}
                </div>
              )}
            </div>

            {/* Step 2: Message */}
            <div style={s.card}>
              <div style={s.sectionTitle}>Step 2 — Message</div>
              <div style={{ marginBottom: '16px' }}>
                <label style={s.label}>Notification Title *</label>
                <input style={s.input} type="text"
                  placeholder="e.g. Special offer for you"
                  maxLength={TITLE_MAX}
                  value={title}
                  onChange={function(e) { setTitle(e.target.value); }} />
                <div style={s.charCount}>{title.length} / {TITLE_MAX}</div>
              </div>
              <div>
                <label style={s.label}>Message *</label>
                <textarea style={s.textarea}
                  placeholder="Type your push message…"
                  maxLength={MESSAGE_MAX}
                  value={message}
                  onChange={function(e) { setMessage(e.target.value); }} />
                <div style={s.charCount}>{message.length} / {MESSAGE_MAX}</div>
              </div>
            </div>

            {/* Step 3: Send — Announcements.js lines 254-285 */}
            <div style={s.card}>
              <div style={s.sectionTitle}>Step 3 — Send</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                <button onClick={handleSend} disabled={!canSend}
                  style={{ ...s.sendBtn, opacity: canSend ? 1 : 0.5, cursor: canSend ? 'pointer' : 'not-allowed' }}>
                  {sending ? 'Sending…' : 'Send Push Campaign'}
                </button>
                {estimate !== null && !sending && (
                  <span style={{ fontSize: '13px', color: '#999' }}>
                    Will reach ~{estimate} consenting recipient{estimate !== 1 ? 's' : ''}
                  </span>
                )}
              </div>
              <div style={{ marginTop: '10px', fontSize: '12px', color: '#bbb' }}>
                Limited to 5 push campaigns per centre per day.
              </div>
            </div>
          </div>
        )}

        {/* HISTORY TAB — Announcements.js lines 357-398, table style: PromoManager.js lines 195-203 / Finance.js lines 516-523 */}
        {activeTab === 'history' && (
          <div>
            {historyLoading ? (
              <div style={{ textAlign: 'center', padding: '60px', color: '#999', fontSize: '14px' }}>Loading history…</div>
            ) : history.length === 0 ? (
              <div style={{ ...s.card, textAlign: 'center', padding: '60px', color: '#bbb' }}>
                <div style={{ fontSize: '15px', marginBottom: '6px' }}>No push campaigns sent yet</div>
                <div style={{ fontSize: '13px' }}>Past campaigns will appear here after you send them.</div>
              </div>
            ) : (
              <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: '8px', overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr style={{ background: '#f9faf7' }}>
                      {['Date', 'Segment', 'Title', 'Reach', 'Sent By'].map(function(h) {
                        return <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: '10px', fontWeight: '700', color: '#666', textTransform: 'uppercase', letterSpacing: '1px', borderBottom: '1px solid #e0e0e0', whiteSpace: 'nowrap' }}>{h}</th>;
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {history.map(function(c) {
                      return (
                        <tr key={c._id} style={{ borderBottom: '1px solid #f5f5f5' }}>
                          <td style={{ padding: '12px 16px', color: '#555', whiteSpace: 'nowrap' }}>
                            {new Date(c.sentAt).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                          </td>
                          <td style={{ padding: '12px 16px', color: '#333', fontWeight: '600' }}>{segLabel(c.segment)}</td>
                          <td style={{ padding: '12px 16px', color: '#1a1a2e', fontWeight: '600', maxWidth: '280px' }}>{c.title}</td>
                          <td style={{ padding: '12px 16px' }}>
                            <span style={{ background: '#eafaf1', color: '#27ae60', borderRadius: '10px', padding: '3px 10px', fontSize: '11px', fontWeight: '700' }}>
                              {c.sentCount ?? 0} sent
                            </span>
                          </td>
                          <td style={{ padding: '12px 16px', color: '#555' }}>
                            {c.sentBy?.firstName ? c.sentBy.firstName + ' ' + (c.sentBy.lastName || '') : '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
}
